import { FunctionComponent, useState } from "react";

import { AutoCompleteItem as DropdownItem, Dropdown } from "@/components/Dropdown/Dropdown";
import {
	useRefreshDynamicsValueMapMutation,
	useSetDynamicsValueMapEntryMutation,
} from "@/pages/Private/redux/dynamics/dynamicsValueMap.api";

import { MsdynamicsField, MsdynamicsFieldMapping, MsdynamicsValueMap } from "../../schema/dynamics";
import { CountryValueMapEditor } from "./CountryValueMapEditor";
import { isInlineValueMap, unplacedLabels, valueMapRows } from "./valueMapDisplay";

type Ts = (key: string, options?: Record<string, unknown>) => string;

interface DynamicsValueMapPanelProps {
	mapping: MsdynamicsFieldMapping & { valueMap: MsdynamicsValueMap };
	targetField: MsdynamicsField | undefined;
	ts: Ts;
}

const NOTHING = "__nothing__";

/**
 * What a value-mapped row writes (PREC-4363): "male → Herr (100000000)", the target options the
 * resolver left unassigned, and a per-key edit — an option for a picklist, a word for a text field.
 * A user's choice is stored as USER and survives every refresh.
 */
export const DynamicsValueMapPanel: FunctionComponent<DynamicsValueMapPanelProps> = ({
	mapping,
	targetField,
	ts,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [refresh, { isLoading: isRefreshing }] = useRefreshDynamicsValueMapMutation();
	const [setEntry] = useSetDynamicsValueMapEntryMutation();
	const { valueMap } = mapping;
	const rows = valueMapRows(valueMap);
	const unplaced = unplacedLabels(valueMap);
	// A key without its own copy (a country code) is shown as it is.
	const keyLabel = (key: string) => {
		const label = ts(`valueMap.keys.${key}`);

		return label.includes("valueMap.keys") ? key : label;
	};
	const options: DropdownItem[] = [
		{ id: NOTHING, title: ts("valueMap.writeNothing") },
		...(targetField?.options ?? []).map((option) => ({
			id: option.value,
			title: `${option.label} (${option.value})`,
		})),
	];

	const save = (sourceKey: string, targetValue: string, targetLabel: string | null) =>
		setEntry({ id: mapping.id, sourceKey, targetValue, targetLabel });

	return (
		<div
			className="ml-1 mt-1 text-xs text-text-meta flex flex-col gap-1"
			data-test={`dynamics-value-map-${mapping.id}`}
		>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
				<span className="font-medium">{ts("valueMap.title")}:</span>
				{isInlineValueMap(valueMap) ? (
					rows.map((row) => (
						<span key={row.sourceKey} data-test={`dynamics-value-map-entry-${row.sourceKey}`}>
							{keyLabel(row.sourceKey)} → {row.display ?? ts("valueMap.unmappedKey")}
						</span>
					))
				) : (
					<span data-test={`dynamics-value-map-summary-${mapping.id}`}>
						{valueMap.target === "TEXT" && valueMap.format
							? ts(`valueMap.format.${valueMap.format}`)
							: ts(`valueMap.coverage.${valueMap.kind}`, { count: valueMap.entries.length })}
					</span>
				)}
				<button
					className="text-brand-light hover:underline"
					disabled={isRefreshing}
					type="button"
					onClick={() => refresh(mapping.id)}
				>
					{ts("valueMap.refresh")}
				</button>
				<button
					className="text-brand-light hover:underline"
					type="button"
					onClick={() => setIsEditing((value) => !value)}
				>
					{isEditing ? ts("valueMap.done") : ts("valueMap.edit")}
				</button>
			</div>
			{unplaced.length > 0 && (
				<span>{ts("valueMap.unplaced", { labels: unplaced.join(", ") })}</span>
			)}
			{isEditing && !isInlineValueMap(valueMap) && (
				<CountryValueMapEditor
					mappingId={mapping.id}
					options={targetField?.options}
					ts={ts}
					valueMap={valueMap}
					onSave={save}
				/>
			)}
			{isEditing &&
				isInlineValueMap(valueMap) &&
				rows.map((row) => (
					<div key={row.sourceKey} className="flex items-center gap-2">
						<span className="w-[90px]">{keyLabel(row.sourceKey)}</span>
						{valueMap.target === "TEXT" ? (
							<input
								className="input-styles !py-1 !text-xs"
								data-test={`dynamics-value-map-input-${row.sourceKey}`}
								defaultValue={row.entry?.targetValue ?? ""}
								onBlur={(event) => save(row.sourceKey, event.target.value.trim(), null)}
							/>
						) : (
							<div className="w-[260px]">
								<Dropdown
									data={options}
									defaultValue={options.find(
										(option) => option.id === (row.entry?.targetValue || NOTHING)
									)}
									floating={true}
									handleSelect={(item) => {
										const option = targetField?.options?.find(
											(candidate) => candidate.value === item.id
										);

										save(
											row.sourceKey,
											item.id === NOTHING ? "" : String(item.id),
											option?.label ?? null
										);
									}}
									label=""
								/>
							</div>
						)}
					</div>
				))}
		</div>
	);
};
